import { observable, action, computed } from 'mobx';
import Alert from 'react-s-alert';

import { App, ArrayApp, ModelRegistry, AppRegistry } from 'core/index';
import { routerStore } from 'core/routing';
import { analytics } from 'core/analytics';
import { FormStore } from 'libs/forms/app';
import { maxWidths } from 'legacyComponents/styled/styleUtils';

import { PermissionedModel } from 'apps/permissions/app';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { redirectToContent, raiseUserError } from 'apps/utils';
import { FileModel, FileApp } from 'apps/file/app';
import { TagModel, TagsApp } from 'apps/tag/app';
import { conf as tagsConf } from 'apps/tag/config.yaml';
import { conf as globalConf } from 'apps/config.yaml';

import { conf } from 'apps/story/config.yaml';

import i18next from 'i18next';

const { imageDefaults } = globalConf;

export class StoryModel extends PermissionedModel {
  static registryName = 'StoryModel';
  static ModelProps = {
    tags: [TagModel],
    avatar: FileModel,
    creatorProfile: 'ProfileModel',
    inSpace: 'SpaceModel',
    content: 'ContentModel',
  };

  @observable id = '';
  @observable title = '';
  @observable avatar = null;
  @observable body = '';
  @observable bodySnippet = '';
  @observable readLength = '';
  @observable creatorProfile = null;
  @observable dateCreated = '';
  @observable lastUpdated = '';
  @observable slug = '';
  @observable tags = [];
  @observable comments = [];
  @observable space = null;
  @observable inSpace = null;
  @observable content = null;

  // FIXME
  // eslint-disable-next-line getter-return,consistent-return
  @computed get spacePath() {
    // TODO: make this generic for none sub-space scenarios?
    if (this.space) {
      let prefix;
      if (this.space.spaceType === 'PROFILE') {
        prefix = '@';
      } else if (this.space.spaceType === 'INITIATIVE') {
        prefix = '+';
      }
      return prefix + this.space.content.slug;
    }
  }

  @computed get imageFallbackPath() {
    return imageDefaults.story;
  }

  @computed get commentsCount() {
    // Isn't comments a connection? Why the attribute here?
    if (this.comments && this.comments.totalCount) {
      return this.comments.totalCount;
    }
    return 0;
  }

  @computed get isOwnStory() {
    // NOTE: Still makes sense to keep this, even with permissioned Models.
    // Used to determine whether to show follow button in Author Details.
    if (clientStore.signedInProfile && this.creatorProfile) {
      if (
        this.creatorProfile.username === clientStore.signedInProfile.username
      ) {
        return true;
      }
    }
    return false;
  }
}
ModelRegistry.add(StoryModel);

export class StoryApp extends App {
  static registryName = 'StoryApp';
  static AppProps = {
    tags: TagsApp,
    avatar: FileApp,
    creatorProfile: 'ProfileApp',
    inSpace: 'SpaceApp',
    content: 'ContentApp',
  };

  static Model = StoryModel;
  @observable editing = true;
  @observable storyForm;
  @observable publishForm;

  @computed get route() {
    if (!(this.space && this.space.content && this.slug)) return {};
    const spaceType = this.space.spaceType.toLowerCase();
    const spaceSymbol = spaceType === 'initiative' ? '+' : '@';
    return {
      name: `${spaceType}.story`,
      params: {
        slug: this.slug,
        [spaceType]: `${spaceSymbol}${this.space.content.slug}`,
      },
    };
  }

  @computed get isNew() {
    return !this.id;
  }

  @action createForms = () => {
    this.storyForm = new FormStore(this, conf, 'storyForm');
    if (this.isNew) {
      this.publishForm = new FormStore(this, conf, 'publishForm');
    }
  };

  @action UpdateTitle(title) {
    this.title = title;
  }

  @action Consolidate = () => {
    if (this.isNew) {
      this.manager.Publish(this);
    } else {
      this.manager.Save(this);
    }
  };

  @action Save = () => {
    if (this.publishForm?.posting || this.storyForm?.posting) {
      return;
    }

    if (!this.storyForm.isValid) {
      this.storyForm.alert();
      return;
    }
    if (this.isNew && !this.publishForm.isValid) {
      this.publishForm.alert();
      return;
    }
    this.storyForm.SetPosting(true);
    clientStore.SetPosting(true);
    if (this.isNew) {
      this.publishForm.SetPosting(true);
    }

    clientStore.ScrollToTop();
    clientStore.SetFooterHidden(false);
    const avatarField = this.storyForm.fieldStores.get('avatar');

    if (avatarField && avatarField.fileModified) {
      // Check that the target space for the avatar file is set.
      if (!avatarField.fileAppManager.targetSpaceId) {
        avatarField.fileAppManager.targetSpaceId = this.inSpace.id;
      }
      avatarField.getUpdate(this.Consolidate);
    } else {
      this.Consolidate();
    }
  };

  @action EditTags = () => {
    this.editTagsFormStore = new FormStore(this, tagsConf, 'editStoryTags');
  };

  @action DoneEditingTags = () => {
    modalStore.ClearComponent();
    this.editTagsFormStore = null;
  };

  @action Cancel = () => {
    clientStore.ScrollToTop();
    clientStore.SetFooterHidden(false);
    this.ResetLocals();
    redirectToContent(this, 'story');
  };

  @action onPublishErr = error => {
    this.publishForm.SetPosting(false);
    clientStore.SetPosting(false);
    raiseUserError(error, 'publish', 'Story');
  };

  @action onPublishSuccess = result => {
    this.publishForm.SetPosting(false);
    this.publishForm.clearDraft();
    this.storyForm.SetPosting(false);
    this.storyForm.clearDraft();
    clientStore.SetPosting(false);
    modalStore.ClearComponent();
    redirectToContent(result, 'story');
    Alert.success(i18next.t('Your Story has been published successfully.'));
    analytics.sendEvent('published-story');
  };

  @action onSaveStoryErr = error => {
    this.storyForm.SetPosting(false);
    clientStore.SetPosting(false);
    raiseUserError(error, 'save', 'Story');
  };

  @action onSaveStorySuccess = result => {
    this.storyForm.SetPosting(false);
    this.storyForm.clearDraft();
    clientStore.SetPosting(false);
    clientStore.ScrollToTop();
    Alert.success(i18next.t('Your Story has been updated successfully.'));
    this.ResetLocals();
    redirectToContent(result, 'story');
  };

  @action Delete = () => {
    this.manager.Delete(this);
  };

  @action onDeleteErr = error => {
    this.ResetLocals();
    raiseUserError(error, 'delete', 'Story');
  };

  @action onDeleteSuccess = () => {
    this.ResetLocals();
    routerStore.router.navigate('home');
    Alert.success(i18next.t('Your Story has been deleted successfully.'));
  };
}

AppRegistry.add(StoryApp);

export class StoryCardLayout {
  // FIXME - default layout
  // eslint-disable-next-line getter-return,consistent-return
  @computed get layoutSpec() {
    if (clientStore.windowWidth >= maxWidths.m) {
      return { cardColumns: 2, initialRows: 1, extraRows: 2 };
    }
    if (clientStore.windowWidth < maxWidths.m) {
      return { cardColumns: 1, initialRows: 3, extraRows: 3 };
    }
    if (clientStore.windowWidth < maxWidths.xs) {
      return { cardColumns: 1, initialRows: 3, extraRows: 3 };
    }
  }

  @computed get initialBatchSize() {
    return this.layoutSpec.initialRows * this.layoutSpec.cardColumns;
  }

  @computed get batchSize() {
    return this.layoutSpec.extraRows * this.layoutSpec.cardColumns;
  }
}

export class StoryArrayApp extends ArrayApp {
  static registryName = 'StoryArrayApp';
  static ElementApp = StoryApp;
  cardLayout = new StoryCardLayout();
}
